/**
 * Knowledge → one article (9 Oct): read it, edit it (Markdown with a live
 * preview), publish / unpublish / retire it, retry its indexing, and see its
 * history with a diff between any version and the one before it.
 *
 * `/knowledge/articles/new` is the same page, empty, for a new draft. Every
 * IT user reads and may leave a review comment; editors change. Nothing waits
 * for an approver (9 Oct, product owner).
 */

import { Archive, Eye, Icon, Pencil, RefreshCw, RotateCcw, Send } from "@norican/icons";
import {
  Alert,
  Badge,
  Button,
  Card,
  Field,
  Input,
  Tabs,
  Textarea,
  Typography,
} from "@norican/react";
import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

import {
  AppBreadcrumbs,
  ErrorState,
  LoadingRows,
  Markdown,
  PageContainer,
  RelativeTime,
  SectionCard,
} from "@/components/common";
import { useDocumentTitle } from "@/hooks";
import { useT } from "@/i18n";
import type { KnowledgeArticle, KnowledgeArticleInput, KnowledgeVersion } from "@/lib/api";

import {
  useCommentKnowledgeArticle,
  useCreateKnowledgeArticle,
  useKnowledgeAreas,
  useKnowledgeArticle,
  useKnowledgeVersions,
  useReindexKnowledgeArticle,
  useSetKnowledgeStatus,
  useUpdateKnowledgeArticle,
} from "./api";
import { areaNames, diffCounts, lineDiff, parseTags } from "./lib/knowledge";
import {
  AreaPicker,
  AudienceBadge,
  IndexBadge,
  KnowledgeGate,
  OptionSelect,
  ReviewBadge,
  StatusBadge,
} from "./shared";

const SOURCE_TYPES = ["manual", "upload", "sharepoint_export", "ivanti_ticket", "import"] as const;

export function KnowledgeArticlePage() {
  const { t } = useT();
  useDocumentTitle(t("knowledgeAdmin.articles.title"));
  return (
    <KnowledgeGate title={t("knowledgeAdmin.articles.title")}>
      <ArticleBody />
    </KnowledgeGate>
  );
}

function ArticleBody() {
  const { t } = useT();
  const { articleId = "new" } = useParams();
  const isNew = articleId === "new";
  const article = useKnowledgeArticle(isNew ? undefined : articleId);
  const [editing, setEditing] = useState(isNew);

  const crumbs = (
    <AppBreadcrumbs
      items={[
        { label: t("knowledgeAdmin.overview.title"), to: "/knowledge" },
        { label: t("knowledgeAdmin.articles.title"), to: "/knowledge/articles" },
        { label: isNew ? t("knowledgeAdmin.articles.new") : articleId },
      ]}
    />
  );

  if (isNew) {
    return (
      <PageContainer title={t("knowledgeAdmin.articles.new")} breadcrumbs={crumbs} width="wide">
        <ArticleEditor article={null} onDone={() => undefined} />
      </PageContainer>
    );
  }
  if (article.isLoading) {
    return (
      <PageContainer breadcrumbs={crumbs} width="wide">
        <LoadingRows rows={8} />
      </PageContainer>
    );
  }
  if (article.error != null || article.data === undefined) {
    return (
      <PageContainer breadcrumbs={crumbs} width="wide">
        <ErrorState error={article.error} onRetry={() => void article.refetch()} />
      </PageContainer>
    );
  }
  const data = article.data;
  return (
    <PageContainer
      title={data.title}
      subtitle={data.summary ?? undefined}
      breadcrumbs={crumbs}
      width="wide"
      actions={<StatusActions article={data} editing={editing} onEdit={() => setEditing(true)} />}
    >
      {editing ? (
        <ArticleEditor article={data} onDone={() => setEditing(false)} />
      ) : (
        <ArticleView article={data} />
      )}
    </PageContainer>
  );
}

function StatusActions({
  article,
  editing,
  onEdit,
}: {
  article: KnowledgeArticle;
  editing: boolean;
  onEdit: () => void;
}) {
  const { t } = useT();
  const status = useSetKnowledgeStatus();
  const reindex = useReindexKnowledgeArticle();
  if (!article.can_edit || editing) {
    return null;
  }
  const run = (action: "publish" | "unpublish" | "retire") =>
    status.mutate({ id: article.id, body: { action } });
  return (
    <div className="flex flex-wrap gap-2">
      <Button variant="outline" startIcon={<Icon icon={Pencil} size="sm" />} onClick={onEdit}>
        {t("knowledgeAdmin.article.edit")}
      </Button>
      {article.status !== "published" ? (
        <Button
          startIcon={<Icon icon={Send} size="sm" />}
          loading={status.isPending}
          onClick={() => run("publish")}
        >
          {t("knowledgeAdmin.article.publish")}
        </Button>
      ) : (
        <Button
          variant="outline"
          startIcon={<Icon icon={RotateCcw} size="sm" />}
          loading={status.isPending}
          onClick={() => run("unpublish")}
        >
          {t("knowledgeAdmin.article.unpublish")}
        </Button>
      )}
      {article.status !== "retired" ? (
        <Button
          variant="outline"
          tone="danger"
          startIcon={<Icon icon={Archive} size="sm" />}
          loading={status.isPending}
          onClick={() => run("retire")}
        >
          {t("knowledgeAdmin.article.retire")}
        </Button>
      ) : null}
      {article.status === "published" && article.index_state !== "indexed" ? (
        <Button
          variant="outline"
          startIcon={<Icon icon={RefreshCw} size="sm" />}
          loading={reindex.isPending}
          onClick={() => reindex.mutate(article.id)}
        >
          {t("knowledgeAdmin.articles.retryIndexing")}
        </Button>
      ) : null}
    </div>
  );
}

function ArticleView({ article }: { article: KnowledgeArticle }) {
  const { t } = useT();
  const areas = useKnowledgeAreas();
  const [tab, setTab] = useState("article");
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge status={article.status} />
        <ReviewBadge state={article.review_state} />
        {article.status === "published" ? <IndexBadge state={article.index_state} /> : null}
        <AudienceBadge audience={article.audience} />
        {areaNames(article.areas ?? [], areas.data?.items ?? []).map((name) => (
          <Badge key={name} tone="accent" variant="subtle" size="sm">
            {name}
          </Badge>
        ))}
        {(article.tags ?? []).map((tag) => (
          <Badge key={tag} tone="neutral" variant="outline" size="sm">
            #{tag}
          </Badge>
        ))}
      </div>
      {article.index_state === "failed" ? (
        <Alert tone="warning" title={t("knowledgeAdmin.article.notIndexedTitle")}>
          {t("knowledgeAdmin.article.notIndexed")}
        </Alert>
      ) : null}
      <Tabs.Root value={tab} onValueChange={(value) => setTab(String(value))}>
        <Tabs.List aria-label={t("knowledgeAdmin.article.tabs")}>
          <Tabs.Tab value="article">{t("knowledgeAdmin.article.tabArticle")}</Tabs.Tab>
          <Tabs.Tab value="history">{t("knowledgeAdmin.article.tabHistory")}</Tabs.Tab>
          <Tabs.Indicator />
        </Tabs.List>
        <Tabs.Panel value="article" className="pt-4">
          <div className="grid gap-6 lg:grid-cols-[1fr_280px]">
            <Card variant="outline" padding="lg">
              <Markdown>{article.body_markdown}</Markdown>
            </Card>
            <ArticleFacts article={article} />
          </div>
        </Tabs.Panel>
        <Tabs.Panel value="history" className="pt-4">
          <History articleId={article.id} enabled={tab === "history"} />
        </Tabs.Panel>
      </Tabs.Root>
    </div>
  );
}

function ArticleFacts({ article }: { article: KnowledgeArticle }) {
  const { t } = useT();
  const rows: [string, React.ReactNode][] = [
    [t("knowledgeAdmin.field.id"), article.id],
    [t("knowledgeAdmin.field.owner"), article.owner_name ?? article.owner_email ?? "—"],
    [t("knowledgeAdmin.field.reviewer"), article.reviewer_name ?? article.reviewer_email ?? "—"],
    [t("knowledgeAdmin.field.reviewDue"), article.review_due ?? "—"],
    [t("knowledgeAdmin.field.source"), t(`knowledgeAdmin.source.${article.source_type}`)],
    [t("knowledgeAdmin.field.sourceRef"), article.source_ref ?? "—"],
    [t("knowledgeAdmin.field.version"), String(article.version)],
    [
      t("knowledgeAdmin.field.updated"),
      <span key="u">
        <RelativeTime value={article.updated_at} /> · {article.updated_by}
      </span>,
    ],
  ];
  return (
    <SectionCard title={t("knowledgeAdmin.article.facts")} padding="sm">
      <dl className="flex flex-col gap-2">
        {rows.map(([label, value]) => (
          <div key={label} className="flex flex-col">
            <dt className="text-fg-muted text-xs">{label}</dt>
            <dd className="m-0 text-sm break-words">{value}</dd>
          </div>
        ))}
      </dl>
    </SectionCard>
  );
}

function History({ articleId, enabled }: { articleId: string; enabled: boolean }) {
  const { t } = useT();
  const versions = useKnowledgeVersions(articleId, enabled);
  const comment = useCommentKnowledgeArticle();
  const [text, setText] = useState("");
  const [open, setOpen] = useState<number | null>(null);
  const items = versions.data?.items ?? [];
  const snapshots = items.filter((v) => v.action !== "comment");

  return (
    <div className="flex flex-col gap-4">
      <SectionCard
        title={t("knowledgeAdmin.history.comment")}
        description={t("knowledgeAdmin.history.commentHint")}
      >
        <div className="flex flex-col gap-2">
          <Textarea
            rows={2}
            autoResize
            value={text}
            maxLength={4000}
            onChange={(event) => setText(event.target.value)}
            aria-label={t("knowledgeAdmin.history.comment")}
          />
          <div>
            <Button
              size="sm"
              disabled={text.trim() === ""}
              loading={comment.isPending}
              onClick={() =>
                comment.mutate({ id: articleId, comment: text }, { onSuccess: () => setText("") })
              }
            >
              {t("knowledgeAdmin.history.addComment")}
            </Button>
          </div>
        </div>
      </SectionCard>
      {versions.isLoading ? (
        <LoadingRows rows={4} />
      ) : versions.error != null ? (
        <ErrorState error={versions.error} onRetry={() => void versions.refetch()} />
      ) : (
        <ol className="flex list-none flex-col gap-2 p-0">
          {items.map((entry) => {
            const previous = snapshots.find(
              (v) => v.id < entry.id && v.action !== "comment" && entry.action !== "comment",
            );
            return (
              <li key={entry.id}>
                <Card variant="subtle" padding="sm">
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <Badge tone="neutral" variant="outline" size="sm">
                      {t(`knowledgeAdmin.action.${entry.action as "edit"}`, {
                        defaultValue: entry.action,
                      })}
                    </Badge>
                    {entry.action === "comment" ? null : (
                      <span className="text-fg-muted">v{entry.version}</span>
                    )}
                    <span>{entry.created_by}</span>
                    <span className="text-fg-muted">
                      <RelativeTime value={entry.created_at} />
                    </span>
                    {previous ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        startIcon={<Icon icon={Eye} size="sm" />}
                        onClick={() => setOpen(open === entry.id ? null : entry.id)}
                      >
                        {open === entry.id
                          ? t("knowledgeAdmin.history.hideChanges")
                          : t("knowledgeAdmin.history.showChanges")}
                      </Button>
                    ) : null}
                  </div>
                  {entry.note ? (
                    <Typography variant="body-sm" className="mt-1">
                      {entry.note}
                    </Typography>
                  ) : null}
                  {open === entry.id && previous ? (
                    <VersionDiff before={previous} after={entry} />
                  ) : null}
                </Card>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

function VersionDiff({ before, after }: { before: KnowledgeVersion; after: KnowledgeVersion }) {
  const { t } = useT();
  const lines = useMemo(
    () =>
      lineDiff(
        `# ${before.title}\n\n${before.body_markdown}`,
        `# ${after.title}\n\n${after.body_markdown}`,
      ),
    [before, after],
  );
  const counts = diffCounts(lines);
  const changedFields = (
    ["status", "audience", "owner_email", "owner_name", "review_due", "summary"] as const
  ).filter((key) => (before[key] ?? "") !== (after[key] ?? ""));
  return (
    <div className="mt-2 flex flex-col gap-2">
      <Typography variant="caption" tone="muted">
        {t("knowledgeAdmin.history.diffSummary", {
          from: before.version,
          added: counts.added,
          removed: counts.removed,
        })}
        {changedFields.length > 0 ? ` · ${changedFields.join(", ")}` : ""}
      </Typography>
      <pre className="bg-bg max-h-96 overflow-auto rounded p-2 text-xs whitespace-pre-wrap">
        {lines
          .filter((line) => line.kind !== "same")
          .slice(0, 400)
          .map((line, index) => (
            <div
              key={index}
              className={line.kind === "added" ? "text-success-fg" : "text-danger-fg line-through"}
            >
              {line.kind === "added" ? "+ " : "− "}
              {line.text}
            </div>
          ))}
      </pre>
    </div>
  );
}

function ArticleEditor({
  article,
  onDone,
}: {
  article: KnowledgeArticle | null;
  onDone: () => void;
}) {
  const { t } = useT();
  const navigate = useNavigate();
  const areas = useKnowledgeAreas();
  const create = useCreateKnowledgeArticle();
  const update = useUpdateKnowledgeArticle();
  const [form, setForm] = useState<KnowledgeArticleInput>(() => ({
    title: article?.title ?? "",
    summary: article?.summary ?? "",
    body_markdown: article?.body_markdown ?? "",
    tags: article?.tags ?? [],
    areas: article?.areas ?? [],
    audience: article?.audience ?? "everyone",
    owner_email: article?.owner_email ?? "",
    owner_name: article?.owner_name ?? "",
    reviewer_email: article?.reviewer_email ?? "",
    reviewer_name: article?.reviewer_name ?? "",
    review_due: article?.review_due ?? null,
    source_type: article?.source_type ?? "manual",
    source_ref: article?.source_ref ?? "",
    language: article?.language ?? "en",
    note: "",
  }));
  const [tagsText, setTagsText] = useState((article?.tags ?? []).join(", "));
  const set = <K extends keyof KnowledgeArticleInput>(key: K, value: KnowledgeArticleInput[K]) =>
    setForm((current) => ({ ...current, [key]: value }));
  const error = create.error ?? update.error;
  const saving = create.isPending || update.isPending;

  function save() {
    const body = { ...form, tags: parseTags(tagsText) };
    if (article === null) {
      create.mutate(body, {
        onSuccess: (saved) => void navigate(`/knowledge/articles/${saved.id}`, { replace: true }),
      });
    } else {
      update.mutate(
        { id: article.id, body: { ...body, version: article.version } },
        { onSuccess: onDone },
      );
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {article?.status === "published" ? (
        <Alert tone="info">{t("knowledgeAdmin.editor.livePublished")}</Alert>
      ) : null}
      <div className="grid gap-4 md:grid-cols-2">
        <Field name="title" label={t("knowledgeAdmin.field.title")} required>
          <Input
            value={form.title}
            maxLength={300}
            onChange={(e) => set("title", e.target.value)}
          />
        </Field>
        <Field name="summary" label={t("knowledgeAdmin.field.summary")}>
          <Input
            value={form.summary ?? ""}
            maxLength={2000}
            onChange={(e) => set("summary", e.target.value)}
          />
        </Field>
        <Field name="areas" label={t("knowledgeAdmin.field.areas")}>
          <AreaPicker
            areas={areas.data?.items ?? []}
            value={form.areas ?? []}
            onChange={(next) => set("areas", next)}
          />
        </Field>
        <Field
          name="tags"
          label={t("knowledgeAdmin.field.tags")}
          hint={t("knowledgeAdmin.editor.tagsHint")}
        >
          <Input value={tagsText} onChange={(e) => setTagsText(e.target.value)} />
        </Field>
        <Field name="audience" label={t("knowledgeAdmin.field.audience")}>
          <OptionSelect
            options={[
              { value: "everyone", label: t("knowledgeAdmin.audience.everyone") },
              { value: "it", label: t("knowledgeAdmin.audience.it") },
            ]}
            value={form.audience ?? "everyone"}
            onValueChange={(v) => set("audience", v === "it" ? "it" : "everyone")}
            placeholder=""
          />
        </Field>
        <Field name="review_due" label={t("knowledgeAdmin.field.reviewDue")}>
          <Input
            type="date"
            value={form.review_due ?? ""}
            onChange={(e) => set("review_due", e.target.value === "" ? null : e.target.value)}
          />
        </Field>
        <Field name="owner_email" label={t("knowledgeAdmin.field.ownerEmail")}>
          <Input
            type="email"
            value={form.owner_email ?? ""}
            onChange={(e) => set("owner_email", e.target.value)}
          />
        </Field>
        <Field name="owner_name" label={t("knowledgeAdmin.field.ownerName")}>
          <Input
            value={form.owner_name ?? ""}
            onChange={(e) => set("owner_name", e.target.value)}
          />
        </Field>
        <Field
          name="reviewer_email"
          label={t("knowledgeAdmin.field.reviewerEmail")}
          hint={t("knowledgeAdmin.editor.reviewerHint")}
        >
          <Input
            type="email"
            value={form.reviewer_email ?? ""}
            onChange={(e) => set("reviewer_email", e.target.value)}
          />
        </Field>
        <Field name="source_type" label={t("knowledgeAdmin.field.source")}>
          <OptionSelect
            options={SOURCE_TYPES.map((value) => ({
              value,
              label: t(`knowledgeAdmin.source.${value}`),
            }))}
            value={form.source_type ?? "manual"}
            onValueChange={(v) =>
              set("source_type", (v || "manual") as KnowledgeArticleInput["source_type"])
            }
            placeholder=""
          />
        </Field>
        <Field name="source_ref" label={t("knowledgeAdmin.field.sourceRef")}>
          <Input
            value={form.source_ref ?? ""}
            maxLength={1000}
            onChange={(e) => set("source_ref", e.target.value)}
          />
        </Field>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Field
          name="body"
          label={t("knowledgeAdmin.field.body")}
          hint={t("knowledgeAdmin.editor.bodyHint")}
        >
          <Textarea
            rows={22}
            value={form.body_markdown ?? ""}
            onChange={(e) => set("body_markdown", e.target.value)}
            className="font-mono text-sm"
          />
        </Field>
        <div className="flex flex-col gap-1">
          <Typography variant="label">{t("knowledgeAdmin.editor.preview")}</Typography>
          <Card variant="outline" padding="md" className="max-h-[36rem] overflow-auto">
            {(form.body_markdown ?? "").trim() === "" ? (
              <Typography variant="body-sm" tone="muted">
                {t("knowledgeAdmin.editor.previewEmpty")}
              </Typography>
            ) : (
              <Markdown>{form.body_markdown ?? ""}</Markdown>
            )}
          </Card>
        </div>
      </div>

      {article === null ? null : (
        <Field name="note" label={t("knowledgeAdmin.field.note")}>
          <Input
            value={form.note ?? ""}
            maxLength={1000}
            onChange={(e) => set("note", e.target.value)}
            placeholder={t("knowledgeAdmin.editor.notePlaceholder")}
          />
        </Field>
      )}

      {error == null ? null : <ErrorState error={error} variant="inline" />}
      <div className="flex flex-wrap gap-2">
        <Button loading={saving} disabled={form.title.trim() === ""} onClick={save}>
          {article === null ? t("knowledgeAdmin.editor.createDraft") : t("button.save")}
        </Button>
        {article === null ? (
          <Button variant="ghost" render={<Link to="/knowledge/articles" />}>
            {t("button.cancel")}
          </Button>
        ) : (
          <Button variant="ghost" onClick={onDone}>
            {t("button.cancel")}
          </Button>
        )}
      </div>
    </div>
  );
}
